import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck, Clock, Award } from "lucide-react";
import { scrollToId } from "../lib/scroll";

const HERO_IMG =
  "https://images.unsplash.com/photo-1501534664411-d04203736d05?fm=jpg&q=85&w=1600&auto=format&fit=crop";

const lines = [
  { text: "Torna-te motorista", cls: "text-[#171717]" },
  { text: "BOLT", cls: "text-[#34d186]" },
  { text: "com o Grupo IFT.", cls: "text-stroke-bolt" },
];

const chips = [
  { icon: Clock, label: "50h de formação IMT" },
  { icon: Award, label: "Exame incluído" },
  { icon: ShieldCheck, label: "B-learning flexível" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 130]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden pt-32 pb-20 sm:pt-40">
      <div className="pointer-events-none absolute -top-40 right-[-10%] h-[560px] w-[560px] rounded-full bg-[#34d186]/10 blur-[140px]" />
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-5 sm:px-8 lg:grid-cols-12">
        <motion.div style={{ opacity: fade }} className="lg:col-span-7">
          <h1 className="font-display text-4xl font-extrabold leading-[0.98] tracking-tight sm:text-5xl lg:text-7xl" data-testid="hero-headline">
            {lines.map((l, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                  className={`block ${l.cls}`}
                >
                  {l.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            data-testid="hero-subtitle"
            className="mt-7 max-w-xl text-base leading-relaxed text-[#737373] sm:text-lg"
          >
            Faz a tua certificação de motorista TVDE com o Grupo IFT e começa a conduzir na Bolt.
            Formação oficial reconhecida pelo IMT, com acompanhamento do primeiro dia até à estrada.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.95 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-primary-cta"
              onClick={() => scrollToId("inscricao")}
              className="group inline-flex items-center gap-2 rounded-full bg-[#34d186] px-7 py-3.5 font-semibold text-[#ffffff] transition-all duration-300 hover:bg-[#00c46a] hover:shadow-[0_0_36px_rgba(52,209,134,0.5)]"
            >
              Quero ser contactado
              <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1.5" />
            </button>
            <button
              data-testid="hero-secondary-cta"
              onClick={() => scrollToId("precos")}
              className="rounded-full border border-[#34d186]/35 px-7 py-3.5 font-semibold text-[#171717] transition-colors duration-300 hover:border-[#34d186] hover:text-[#34d186]"
            >
              Ver preços
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 1.15 }}
            className="mt-12 flex flex-wrap gap-3"
            data-testid="hero-stat-chips"
          >
            {chips.map((c) => (
              <div key={c.label} className="flex items-center gap-2 rounded-full border border-black/10 bg-black/[0.03] px-4 py-2 text-sm text-[#737373]">
                <c.icon size={14} className="text-[#34d186]" />
                {c.label}
              </div>
            ))}
          </motion.div>
        </motion.div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, clipPath: "inset(8% 8% 8% 8% round 24px)" }}
            animate={{ opacity: 1, scale: 1, clipPath: "inset(0% 0% 0% 0% round 24px)" }}
            transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <motion.img
              style={{ y: imgY }}
              src={HERO_IMG}
              alt="Carro Bolt numa rua de Lisboa, Portugal"
              data-testid="hero-image"
              className="h-[420px] w-full rounded-3xl border border-[#34d186]/25 object-cover shadow-[0_30px_80px_rgba(0,0,0,0.55)] sm:h-[520px]"
            />
            <div className="pointer-events-none absolute inset-0 rounded-3xl bg-gradient-to-t from-[#ffffff]/70 via-transparent to-transparent" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
